import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  OnInit,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  Guid,
  isEmptyArray,
  isNil,
  isNotEmptyArray,
  Nillable,
  valueOrDefault,
} from '@web/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControlComponent,
  Icon,
  PrimengButtonDirective,
  PrimengSelectDirective,
  SelectItem,
} from '@web/shared/ui';
import { SummernoteComponent } from '@web/shared/external';
import { Button } from 'primeng/button';
import { Observable, startWith } from 'rxjs';
import { Select } from 'primeng/select';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
} from 'primeng/accordion';
import {
  AdminDictionaryProductAttributeLabelPipe,
  AdminDictionaryProductAttributeSelectItemsProviderService,
} from '@web/shared/module/dictionary';
import {
  AdminProductAttributeFormGroup,
  AdminProductAttributeItem,
  AdminProductAttributes,
  AdminProductAttributesFormGroup,
} from '../../models/admin-product-details-form';

@Component({
  selector: 'app-admin-product-attributes-control',
  imports: [
    Accordion,
    AccordionContent,
    AccordionHeader,
    AccordionPanel,
    Button,
    CommonModule,
    FormControlComponent,
    PrimengButtonDirective,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Select,
    SummernoteComponent,
    AdminDictionaryProductAttributeLabelPipe,
  ],
  templateUrl: './admin-product-attributes-control.component.html',
  styleUrl: './admin-product-attributes-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdminProductAttributesControlComponent),
      multi: true,
    },
  ],
})
export class AdminProductAttributesControlComponent
  implements ControlValueAccessor, OnInit
{
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly provider = inject(AdminDictionaryProductAttributeSelectItemsProviderService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  /* prettier-ignore */
  protected readonly productAttributeSelectItems$ = this.productAttributeSelectItemsSource();
  protected readonly form = this.buildForm();
  protected readonly Icon = Icon;
  protected readonly isNotEmptyArray = isNotEmptyArray;
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly accordion = viewChild(Accordion);

  protected activeAttributeIndex = 0;

  private readonly destroyRef = inject(DestroyRef);

  private onChange!: (value: Nillable<AdminProductAttributes>) => void;
  private onTouched!: () => void;

  public ngOnInit(): void {
    this.listenToFormValueChanges();
  }

  public writeValue(value: Nillable<AdminProductAttributes>): void {
    this.form.controls.attributes.clear({ emitEvent: false });

    if (isNil(value)) {
      return;
    }

    const { items } = value;

    items.forEach((item) => this.createAndPushNewAttribute({ initial: item }));

    this.updateActiveIndex();
  }

  public registerOnChange(
    fn: (value: Nillable<AdminProductAttributes>) => void,
  ): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    isDisabled
      ? this.form.disable({ emitEvent: false })
      : this.form.enable({ emitEvent: false });
  }

  protected handleRemoveClick(index: number): void {
    this.form.controls.attributes.removeAt(index);
    this.updateActiveIndex();
    this.onTouched();
  }

  protected handleAddClick(): void {
    this.createAndPushNewAttribute({ emitEvent: true });
    this.updateActiveIndex();
    this.onTouched();
  }

  protected attributesControls(): Array<AdminProductAttributeFormGroup> {
    return this.form.controls.attributes.controls;
  }

  protected labelForGroup(
    group: AdminProductAttributeFormGroup,
  ): Observable<Nillable<Guid>> {
    const control = group.controls.productAttributeId;

    return control.valueChanges.pipe(startWith(control.value));
  }

  private updateActiveIndex(): void {
    this.activeAttributeIndex = this.form.controls.attributes.length - 1;

    this.changeDetectorRef.detectChanges();
  }

  private listenToFormValueChanges(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (value) => {
        const attributes = valueOrDefault(value.attributes, []);

        if (this.form.invalid) {
          this.onChange(null);
          this.onTouched();

          return;
        }

        if (isEmptyArray(attributes)) {
          this.onChange(null);
          this.onTouched();

          return;
        }

        this.onChange(
          new AdminProductAttributes({
            items: attributes.map(({ content, productAttributeId, id }) => ({
              content: valueOrDefault(content, ''),
              id,
              productAttributeId: valueOrDefault(productAttributeId, null),
            })),
          }),
        );
      },
    });
  }

  private buildForm(): AdminProductAttributesFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      attributes: this.builder.array<AdminProductAttributeFormGroup>([]),
    });
  }

  private createAttributeFormGroup(): AdminProductAttributeFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      id: this.builder.control<Nillable<Guid>>(null),
      productAttributeId: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
      content: this.builder.nonNullable.control<string>('', [Validators.required]),
    });
  }

  private createAndPushNewAttribute(
    options: {
      emitEvent?: boolean;
      initial?: AdminProductAttributeItem;
    } = {},
  ): void {
    const formGroup = this.createAttributeFormGroup();
    const { initial, emitEvent = false } = options;

    this.form.controls.attributes.push(formGroup, { emitEvent });

    if (!isNil(initial)) {
      formGroup.patchValue(initial);
    }
  }

  private productAttributeSelectItemsSource(): Observable<SelectItem<Guid>[]> {
    return this.provider.fetchCollectionAsSelectItems('id');
  }
}
